import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Search, X, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import BottomNav from "../components/BottomNav";
import ThemeToggle from "../components/ThemeToggle";
import { SERVICES } from "../lib/services";
import { getSession, clearSession } from "../lib/auth";
import { reverseGeocode, validateDocument, searchGeocode } from "../lib/api";
import TermsModal from "../components/TermsModal";

export default function Home() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [locStatus, setLocStatus] = useState("idle"); // idle | loading | ok | denied
  const [cityLabel, setCityLabel] = useState("");

  const [showTerms, setShowTerms] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    const s = getSession();
    if (!s) {
      navigate("/", { replace: true });
      return;
    }
    setUser(s);
    setIsIOSDevice(/iPhone|iPad|iPod/i.test(navigator.userAgent));
    
    // Recupera localização existente da sessão se houver, evitando sobrescrever a localização manual
    const cachedLoc = sessionStorage.getItem("gp_loc");
    const cachedCity = sessionStorage.getItem("gp_city");
    if (cachedLoc && cachedCity) {
      try {
        setLocation(JSON.parse(cachedLoc));
        setCityLabel(cachedCity);
        setLocStatus("ok");
      } catch (e) {
        requestLocation();
      }
    } else {
      requestLocation();
    }

    // Verifica a situação do cadastro e faturamento (sem cache — sempre valida ao abrir)
    const checkUserStatus = async () => {
      try {
        const res = await validateDocument(s.document);
        if (!res.ok) {
          import("sonner").then(({ toast }) => {
            toast.error(res.detail || "Acesso bloqueado.");
          });
          // Limpa o cache de último check ao bloquear
          localStorage.removeItem(`goprotege_last_status_check_${s.document}`);
          clearSession();
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Erro ao validar status do usuário:", err);
      }
    };
    checkUserStatus();

    // Re-valida status sempre que o aplicativo volta ao primeiro plano (focus/visibility change)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkUserStatus();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Verifica se ja aceitou o termo de uso
    const acceptedKey = `goprotege_terms_accepted_${s.document}`;
    if (localStorage.getItem(acceptedKey) !== "true") {
      setShowTerms(true);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [navigate]);

  const onAcceptTerms = () => {
    const s = getSession();
    const doc = user?.document || s?.document;
    if (doc) {
      localStorage.setItem(`goprotege_terms_accepted_${doc}`, "true");
      setShowTerms(false);
    }
  };

  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      setLocStatus("denied");
      return;
    }
    setLocStatus("loading");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocation(loc);
        sessionStorage.setItem("gp_loc", JSON.stringify(loc));
        setLocStatus("ok");
        try {
          const data = await reverseGeocode(loc.lat, loc.lng);
          setCityLabel(data.label || "");
          sessionStorage.setItem("gp_city", data.label || "");
        } catch (e) {
          // ignore reverse geocode failures - keep location pill empty label
        }
      },
      (err) => {
        setLocStatus("denied");
        // err.code === 1 means PERMISSION_DENIED (blocked by user)
        if (err.code === 1) {
          setShowLocationModal(true);
        }
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSearchAddress = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const results = await searchGeocode(searchQuery);
      setSearchResults(results || []);
      if (!results || results.length === 0) {
        import("sonner").then(({ toast }) => {
          toast.info("Nenhum endereço encontrado. Tente digitar de outra forma.");
        });
      }
    } catch (err) {
      console.error("Erro ao buscar endereço:", err);
      import("sonner").then(({ toast }) => {
        toast.error("Erro ao pesquisar endereço. Verifique sua conexão.");
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectAddress = (item) => {
    const loc = { lat: item.lat, lng: item.lng };
    setLocation(loc);
    sessionStorage.setItem("gp_loc", JSON.stringify(loc));
    setCityLabel(item.display_name);
    sessionStorage.setItem("gp_city", item.display_name);
    setLocStatus("ok");
    setShowManualModal(false);
    setSearchQuery("");
    setSearchResults([]);
    import("sonner").then(({ toast }) => {
      toast.success("Localização atualizada com sucesso!");
    });
  };

  const onSelectService = (svcId) => {
    if (!location) {
      // try one more time
      requestLocation();
    }
    navigate(`/servicos/${svcId}`);
  };

  const firstName = user?.name ? user.name.split(" ")[0] : "cliente";

  return (
    <div className="app-shell pb-24" data-testid="home-screen">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 py-3 flex items-center justify-between">
        <Logo size="sm" subtitle />
        <ThemeToggle />
      </header>

      <main className="px-5 pt-6 pb-8 space-y-6">
        <div className="fade-in">
          <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white" data-testid="greeting-title">
            Olá, {firstName} 👋
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Como podemos te ajudar hoje?</p>
        </div>

        {/* Location pill */}
        <div
          onClick={() => setShowManualModal(true)}
          className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 px-4 py-3 shadow-sm fade-in cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
          data-testid="location-pill"
        >
          <div className="h-9 w-9 rounded-full bg-[#00D285]/10 flex items-center justify-center">
            <MapPin size={16} className="text-[#00D285]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wider font-semibold">
              Sua localização
            </div>
            <div className="text-[13px] font-medium text-slate-700 dark:text-slate-200 line-clamp-2 leading-snug">
              {locStatus === "ok" && location
                ? (cityLabel || "Detectando cidade...")
                : locStatus === "loading"
                ? "Detectando..."
                : locStatus === "denied"
                ? "Permita o acesso à localização"
                : "Aguardando..."}
            </div>
          </div>
          {locStatus !== "ok" && (
            <button
              data-testid="enable-location-button"
              onClick={(e) => {
                e.stopPropagation();
                requestLocation();
              }}
              className="text-xs font-semibold text-[#00D285] hover:underline"
            >
              Ativar
            </button>
          )}
        </div>

        {/* Service grid */}
        <div className="grid grid-cols-2 gap-3 stagger" data-testid="services-grid">
          {SERVICES.map((svc) => {
            const Icon = svc.icon;
            return (
              <button
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                onClick={() => onSelectService(svc.id)}
                className="btn-press fade-in bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-[#00D285]/60 hover:shadow-md rounded-2xl p-5 flex flex-col items-center justify-center gap-3 transition-all"
              >
                <div className="h-14 w-14 rounded-2xl bg-[#00D285]/10 flex items-center justify-center">
                  <Icon size={28} className="text-[#00D285]" strokeWidth={2} />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{svc.label}</div>
                  <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{svc.subtitle}</div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      <TermsModal 
        isOpen={showTerms} 
        onAccept={onAcceptTerms} 
      />

      {/* Modal de Ajuda de Permissão de Localização */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-50 flex flex-col justify-center p-4 fade-in" onClick={() => setShowLocationModal(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800 text-slate-850 dark:text-slate-200 animate-slide-up flex flex-col items-center gap-4 max-w-md mx-auto w-full relative" onClick={(e) => e.stopPropagation()}>
            <div className="h-12 w-12 bg-red-50 dark:bg-red-950/20 text-red-500 rounded-full flex items-center justify-center animate-bounce mb-2">
              <MapPin size={24} />
            </div>
            
            <h3 className="text-lg font-bold text-slate-900 dark:text-white text-center">Permissão de Localização Bloqueada</h3>
            
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center leading-relaxed">
              O acesso à localização está bloqueado no seu navegador. Siga estes passos simples para ativar e encontrar socorros próximos a você:
            </p>
            
            <div className="space-y-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300 w-full mt-2">
              {isIOSDevice ? (
                <>
                  <div className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">1</span>
                    <span>Toque no ícone de configurações <strong>"aA"</strong> no lado esquerdo da barra de endereço do Safari.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">2</span>
                    <span>Selecione <strong>"Ajustes do Site"</strong> (Site Settings).</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">3</span>
                    <span>Altere a opção de <strong>"Localização"</strong> para <strong>"Permitir"</strong> (ou "Perguntar").</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">1</span>
                    <span>Toque no ícone de <strong>cadeado</strong> ou <strong>configurações</strong> no lado esquerdo da barra de endereço do seu navegador.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">2</span>
                    <span>Selecione a opção <strong>"Permissões do site"</strong> ou <strong>"Configurações"</strong>.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">3</span>
                    <span>Ative o acesso de <strong>"Localização"</strong> mudando para <strong>"Permitido"</strong>.</span>
                  </div>
                </>
              )}
            </div>

            <div className="text-[11px] text-slate-400 dark:text-slate-500 text-center mt-2">
              Após permitir, basta atualizar a página para localizar os prestadores.
            </div>

            <button 
              onClick={() => {
                setShowLocationModal(false);
                window.location.reload();
              }}
              className="mt-2 w-full bg-[#00D285] hover:bg-[#00B874] text-white font-semibold py-4 rounded-2xl shadow-md transition cursor-pointer text-center"
            >
              Recarregar Página
            </button>
          </div>
        </div>
      )}

      {/* Modal de Localização Manual */}
      {showManualModal && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-50 flex flex-col justify-center p-4 fade-in" onClick={() => setShowManualModal(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800 text-slate-850 dark:text-slate-200 animate-slide-up flex flex-col gap-4 max-w-md mx-auto w-full relative" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Definir Localização Manual</h3>
              <button 
                onClick={() => {
                  setShowManualModal(false);
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <X size={16} />
              </button>
            </div>
            
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Digite abaixo sua rua, bairro, cidade ou ponto de referência para localizarmos os socorros mais próximos.
            </p>
            
            <form onSubmit={handleSearchAddress} className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Ex: Av. Paulista, São Paulo"
                  className="w-full bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl py-3 pl-4 pr-10 text-sm focus:outline-none focus:border-[#00D285] dark:text-white"
                  autoFocus
                />
                <Search size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
              <button
                type="submit"
                disabled={searchLoading}
                className="bg-[#00D285] hover:bg-[#00B874] disabled:bg-slate-200 text-white font-semibold px-4 rounded-xl text-sm transition shrink-0"
              >
                {searchLoading ? <Loader2 size={16} className="animate-spin" /> : "Buscar"}
              </button>
            </form>
            
            {/* Search Results */}
            <div className="max-h-60 overflow-y-auto no-scrollbar space-y-1.5 mt-2">
              {searchLoading && (
                <div className="flex items-center justify-center py-8 text-slate-400 text-xs gap-2">
                  <Loader2 size={14} className="animate-spin text-[#00D285]" />
                  Pesquisando endereços...
                </div>
              )}
              
              {!searchLoading && searchResults.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2 px-1">Resultados encontrados:</div>
                  {searchResults.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectAddress(item)}
                      className="w-full text-left p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 border border-slate-100 dark:border-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700 flex items-start gap-2.5 transition"
                    >
                      <MapPin size={14} className="text-[#00D285] shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-700 dark:text-slate-200 leading-normal line-clamp-2">
                        {item.display_name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              
              {!searchLoading && searchResults.length === 0 && searchQuery && (
                <div className="text-center py-6 text-xs text-slate-400">
                  Nenhum resultado para exibir. Tente buscar novamente.
                </div>
              )}
            </div>
            
            {/* Use GPS Button */}
            <div className="border-t border-slate-100 dark:border-slate-800/60 pt-4 mt-2">
              <button
                type="button"
                onClick={() => {
                  setShowManualModal(false);
                  requestLocation();
                }}
                className="w-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 border border-slate-200/50 dark:border-slate-700/50 transition text-sm cursor-pointer"
              >
                <MapPin size={16} className="text-[#00D285]" /> Usar GPS do celular
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
